<script setup lang="ts">
import { DialogModal, NoteComponent, type SharedBudgetPayload } from '@metapic-apps/shared'
import { ref } from 'vue'

import { useDeleteSharedBudgetRequest, useEditSharedBudgetRequest, useGetSharedBudgetStats } from '@/api/shared-budget.api'
import OpenIcon from '@/assets/icons/chevron.svg?component'
import EditIcon from '@/assets/icons/edit.svg?component'
import MoreIcon from '@/assets/icons/more.svg?component'
import RemoveIcon from '@/assets/icons/remove-circle.svg?component'
import SharedBudgetForm from '@/components/CampaignForm/SharedBudgetForm.vue'
import { formatMoneyForAdmin } from '@/lib/formatters/money.formatter'
import { notification, round } from '@/lib/helpers'
import {
  BoxComponent,
  ButtonComponent,
  FlexComponent,
  LoadingComponent,
  ProgressComponent,
  TagComponent,
  TypographyComponent,
} from '@/ui-library'

const props = defineProps<{ advertiserId: number; budgetId: number; onDelete: () => void }>()
const { data, isLoading, getBudget } = useGetSharedBudgetStats({
  advertiserId: props.advertiserId,
  budgetId: props.budgetId,
})
const { editBudget, isLoading: isEditingLoading, error: editError } = useEditSharedBudgetRequest()
const { deleteBudget, isLoading: isDeletingLoading, error: deleteError } = useDeleteSharedBudgetRequest()

const isCampaignsComponentOpen = ref(false)
const isEditBudgetPopupOpen = ref(false)
const isDeletePopupOpen = ref(false)

const saveBudget = async (data: SharedBudgetPayload) => {
  await editBudget(props.advertiserId, data)
  isEditBudgetPopupOpen.value = false
  if (!editError.value) {
    notification.success('The budget was saved successfully')
  }
  await getBudget()
}

const deleteSharedBudget = async () => {
  await deleteBudget(props.advertiserId, props.budgetId)
  isDeletePopupOpen.value = false
  props.onDelete()
  if (!deleteError.value) {
    notification.success('The budget was deleted successfully')
  }
}
</script>

<template>
  <BoxComponent :mb="24">
    <TypographyComponent variant="headline-small">Budget</TypographyComponent>
  </BoxComponent>
  <BoxComponent v-if="isLoading" :p="24">
    <LoadingComponent />
  </BoxComponent>
  <div v-else-if="data && !isLoading" class="container">
    <FlexComponent :gap="24" align="center" justify="space-between" class="header">
      <div>
        <TypographyComponent variant="title-medium"> {{ data.title }} </TypographyComponent>
        <TypographyComponent variant="body-small" color="var(--secondary-48)">
          {{ data.campaigns.length }} {{ `campaign${data.campaigns.length > 1 ? 's' : ''}` }} assigned
        </TypographyComponent>
      </div>
      <FlexComponent :gap="12" align="center">
        <ProgressComponent
          :content="`${formatMoneyForAdmin({ amount: data.consumedBudget, currency: data.currency }, { allowZero: true, notation: 'compact', minimumFractionDigits: 0, maximumFractionDigits: 2 })}/${formatMoneyForAdmin({ amount: data.amount.amount, currency: data.currency }, { allowZero: true, notation: 'compact', minimumFractionDigits: 0, maximumFractionDigits: 2 })}`"
          :tooltip-content="`${formatMoneyForAdmin({ amount: data.consumedBudget, currency: data.currency }, { allowZero: true })} / ${formatMoneyForAdmin({ amount: data.amount.amount, currency: data.currency }, { allowZero: true })} used`"
          :percentage="round((data.consumedBudget / data.amount.amount) * 100, 1)"
          class="header__progress"
        />
        <BoxComponent
          :w="48"
          :h="48"
          :pt="12"
          :pl="12"
          :class="['header__expand-btn', { 'is-open': isCampaignsComponentOpen }]"
          @click="isCampaignsComponentOpen = !isCampaignsComponentOpen"
        >
          <el-icon :size="24"><OpenIcon /></el-icon>
        </BoxComponent>
        <el-dropdown trigger="click" :teleported="false" placement="bottom-start">
          <ButtonComponent variant="secondary" is-icon>
            <el-icon :size="24" class="more-icon">
              <MoreIcon />
            </el-icon>
          </ButtonComponent>
          <template #dropdown>
            <BoxComponent position="relative" :mt="8" :mb="8">
              <el-dropdown-item @click="isEditBudgetPopupOpen = true">
                <FlexComponent :gap="8" align="center">
                  <el-icon :size="24">
                    <EditIcon />
                  </el-icon>
                  <TypographyComponent variant="body-large"> Edit </TypographyComponent>
                </FlexComponent>
              </el-dropdown-item>
              <el-dropdown-item @click="isDeletePopupOpen = true">
                <FlexComponent :gap="8" align="center">
                  <el-icon :size="24" class="remove-icon">
                    <RemoveIcon />
                  </el-icon>
                  <TypographyComponent variant="body-large" color="var(--error)"> Remove </TypographyComponent>
                </FlexComponent>
              </el-dropdown-item>
            </BoxComponent>
          </template>
        </el-dropdown>
      </FlexComponent>
    </FlexComponent>
    <div v-if="isCampaignsComponentOpen" class="budget-list">
      <FlexComponent
        v-for="campaign in data.campaigns"
        :key="campaign.id"
        align="center"
        :gap="24"
        class="budget-list__item"
        justify="space-between"
      >
        <FlexComponent align="center" :gap="8">
          <TagComponent v-if="campaign.status === 'active'" disable-transitions variant="success">Active</TagComponent>
          <TagComponent v-else-if="campaign.status === 'ended'" disable-transitions variant="danger">Ended</TagComponent>
          <div>
            <TypographyComponent is="h4" variant="body-small">{{ campaign.internalName }}</TypographyComponent>
            <TypographyComponent v-if="campaign.budgetLimit" variant="body-small" color="var(--secondary-48)">
              Campaign limit:
              {{
                formatMoneyForAdmin(
                  { amount: campaign.budgetLimit, currency: data.currency },
                  { allowZero: true, notation: 'compact', minimumFractionDigits: 0, maximumFractionDigits: 2 },
                )
              }}
            </TypographyComponent>
          </div>
        </FlexComponent>
        <ProgressComponent
          :content="`${formatMoneyForAdmin({ amount: campaign.consumedBudget, currency: data.currency }, { allowZero: true, notation: 'compact', minimumFractionDigits: 0, maximumFractionDigits: 2 })}/${formatMoneyForAdmin({ amount: campaign.budgetLimit || data.amount.amount, currency: data.currency }, { allowZero: true, notation: 'compact', minimumFractionDigits: 0, maximumFractionDigits: 2 })}`"
          :tooltip-content="`${formatMoneyForAdmin({ amount: campaign.consumedBudget, currency: data.currency }, { allowZero: true })} / ${formatMoneyForAdmin({ amount: campaign.budgetLimit || data.amount.amount, currency: data.currency }, { allowZero: true })} used`"
          :percentage="round((campaign.consumedBudget / data.amount.amount) * 100, 3)"
          :has-limit="Boolean(campaign.budgetLimit)"
          :limit="campaign.budgetLimit && round((campaign.budgetLimit / data.amount.amount) * 100, 3)"
          class="budget-list__item__progress"
        />
      </FlexComponent>
    </div>
    <SharedBudgetForm
      v-model="isEditBudgetPopupOpen"
      :is-loading="isEditingLoading"
      :currency="data.currency"
      :budget="data"
      @save="saveBudget"
    />
    <DialogModal v-if="isDeletePopupOpen" v-model="isDeletePopupOpen" @before-close="() => (isDeletePopupOpen = false)">
      <TypographyComponent variant="headline-medium">Delete budget</TypographyComponent>
      <BoxComponent :mt="16">
        <NoteComponent>There are running campaigns assigned to the budget.</NoteComponent>
      </BoxComponent>
      <BoxComponent :mt="16" :mb="24">
        <TypographyComponent variant="body-large">
          Are you sure to delete the budget? Campaigns will continue running without any limitation.
        </TypographyComponent>
      </BoxComponent>
      <FlexComponent :gap="8" justify="flex-end">
        <ButtonComponent variant="text" :is-disabled="isDeletingLoading" @click="() => (isDeletePopupOpen = false)">Cancel</ButtonComponent>
        <ButtonComponent variant="danger" :is-loading="isDeletingLoading" @click="deleteSharedBudget"> Delete budget </ButtonComponent>
      </FlexComponent>
    </DialogModal>
  </div>
</template>

<style scoped lang="scss">
.container {
  border-radius: 16px;
  border: 1px solid var(--secondary-8);
}

.more-icon {
  transform: rotate(90deg);
}

:deep(.remove-icon) {
  svg path {
    fill: var(--error);
  }
}

.header {
  padding: 16px;
}

.header__progress {
  width: 200px;
}

.header__expand-btn {
  cursor: pointer;

  &.is-open {
    transform: rotate(180deg);
  }
}

.budget-list__item {
  padding: 16px;
  background-color: transparent;
  transition: background-color var(--transition) ease;

  &:last-child {
    border-radius: 0 0 16px 16px;
  }

  &:hover {
    background-color: var(--secondary-8);
  }
}

.budget-list__item__progress {
  width: 160px;
}
</style>
